/* ============================================================================
 * pdfx4.js -- a minimal, self-contained PDF/X-4 (ISO 15930-7) writer.
 *
 * WHY THIS EXISTS
 * ---------------
 * Gelato's file spec: "Files should be exported as PDF/X-4 which supports both
 * sRGB & CMYK objects, with output intent set to GRACoL 2006" + 4 mm bleed.
 *
 * jsPDF cannot produce PDF/X-4. It has no way to write /GTS_PDFXVersion or
 * /Trapped into the Info dictionary, its addMetadata() wraps XMP in a
 * <jspdf:metadata> element that is not valid PDF/X identification, and it emits
 * a %PDF-1.3 header. We were previously bolting an sRGB OutputIntent on with
 * byte-patching + hand-repaired xref offsets, which is fragile and still wasn't
 * conformant (it used subtype /GTS_PDFA1 -- that's the PDF/A tag, not PDF/X).
 *
 * Our PDFs are structurally trivial -- ONE page holding ONE full-bleed JPEG --
 * so writing the file by hand is both shorter and far more predictable than
 * patching a library's output. Every byte offset is known as we go, so the xref
 * table is exact rather than repaired after the fact.
 *
 * WHAT PDF/X-4 REQUIRES (and where each piece is handled below)
 *   1. %PDF-1.6 header ................................ writeHeader()
 *   2. /OutputIntent, subtype /GTS_PDFX, with an
 *      embedded /DestOutputProfile ................... obj 7 + 8
 *   3. /GTS_PDFXVersion (PDF/X-4) in the Info dict .... obj 10
 *   4. /Trapped /False in the Info dict (must be
 *      True or False -- never Unknown) ............... obj 10
 *   5. XMP /Metadata on the catalog carrying
 *      pdfxid:GTS_PDFXVersion, agreeing with the
 *      Info dict ..................................... obj 9
 *   6. /MediaBox + /TrimBox on the page, with
 *      TrimBox contained by BleedBox by MediaBox ..... obj 3
 *   7. All colour device-independent or matching the
 *      output intent ................................. the image is [/ICCBased]
 *      sRGB, and there are NO device-colour operators in the content stream at
 *      all -- see the note on bleed below.
 *   8. No encryption, no JavaScript, no external refs .. n/a, we write it all
 *   9. All fonts embedded ............................. n/a, raster-only
 *
 * THE BLEED, AND WHY THERE IS NO RECTANGLE
 * ----------------------------------------
 * The old exporters drew the 4 mm bleed margin as a filled rect via jsPDF's
 * setFillColor(), which emits an "rg" DeviceRGB operator. Under a CMYK output
 * intent that is uncalibrated device colour and NOT conformant. So instead we
 * pad the raster itself out to the full page and draw one image edge to edge.
 * The content stream is then literally "q cm Do Q" -- no colour operators exist
 * to be non-conformant.
 *
 * Preferably the CALLER renders the bleed: it hands us a canvas that is already
 * trim+bleed (opts.sourceIncludesBleed) with the poster's own background colour
 * and paper texture running off the edge of the sheet. That is the only way the
 * bleed strip is real artwork rather than an approximation of it. When a caller
 * can't do that, padCanvasToBleed() mirrors the outermost millimetres outwards
 * -- a reflection keeps any texture at its true scale, where the stretched
 * single pixel row we used before smeared it into visible streaks.
 *
 * VERIFICATION
 * ------------
 * tools/verify-pdfx4.js checks the structure this file emits (xref offsets,
 * required keys, box containment). That is NOT a conformance claim -- real
 * validation needs Acrobat Preflight or veraPDF against the PDF/X-4 profile.
 * ==========================================================================*/
(function (global) {
  "use strict";

  var MM_TO_PT = 72 / 25.4;
  var PRODUCER = "tildas-poster pdfx4.js";

  // ---- byte plumbing --------------------------------------------------------
  // The PDF body is assembled as a list of byte chunks with a running length, so
  // the offset of every object is known at the moment we write it. That is the
  // whole reason the xref table below needs no patching.

  function latin1(s) {
    var a = new Uint8Array(s.length);
    for (var i = 0; i < s.length; i++) a[i] = s.charCodeAt(i) & 0xff;
    return a;
  }

  function utf8(s) {
    if (global.TextEncoder) return new TextEncoder().encode(s);
    return latin1(unescape(encodeURIComponent(s)));
  }

  function Writer() { this.chunks = []; this.len = 0; }
  Writer.prototype.push = function (u8) {
    this.chunks.push(u8); this.len += u8.length; return this;
  };
  Writer.prototype.raw = function (s) { return this.push(latin1(s)); };
  Writer.prototype.line = function (s) { return this.raw(s + "\n"); };
  Writer.prototype.bytes = function () {
    var out = new Uint8Array(this.len), o = 0;
    for (var i = 0; i < this.chunks.length; i++) {
      out.set(this.chunks[i], o); o += this.chunks[i].length;
    }
    return out;
  };

  // PDF reals must never be written in exponential notation.
  function num(n) {
    if (!isFinite(n)) throw new Error("pdfx4: non-finite number");
    var s = (Math.round(n * 10000) / 10000).toFixed(4);
    s = s.replace(/0+$/, "").replace(/\.$/, "");
    return s === "" || s === "-" ? "0" : s;
  }

  // Literal-string escaping for PDF text strings: ( ) and \ are special.
  function pdfStr(s) {
    return "(" + String(s == null ? "" : s).replace(/[\\()]/g, "\\$&")
      .replace(/[\r\n]/g, " ") + ")";
  }

  function pad(n, width) {
    var s = String(n);
    while (s.length < width) s = "0" + s;
    return s;
  }

  // ---- dates ----------------------------------------------------------------
  // The Info dict wants D:YYYYMMDDHHmmSS+HH'mm'; XMP wants ISO-8601. PDF/X-4
  // requires the two to agree, so both come from one Date.

  function tzParts(d) {
    var off = -d.getTimezoneOffset();            // minutes east of UTC
    var sign = off < 0 ? "-" : "+";
    var a = Math.abs(off);
    return { sign: sign, hh: pad(Math.floor(a / 60), 2), mm: pad(a % 60, 2) };
  }

  function pdfDate(d) {
    var t = tzParts(d);
    return "D:" + d.getFullYear() + pad(d.getMonth() + 1, 2) + pad(d.getDate(), 2) +
      pad(d.getHours(), 2) + pad(d.getMinutes(), 2) + pad(d.getSeconds(), 2) +
      t.sign + t.hh + "'" + t.mm + "'";
  }

  function xmpDate(d) {
    var t = tzParts(d);
    return d.getFullYear() + "-" + pad(d.getMonth() + 1, 2) + "-" + pad(d.getDate(), 2) +
      "T" + pad(d.getHours(), 2) + ":" + pad(d.getMinutes(), 2) + ":" + pad(d.getSeconds(), 2) +
      t.sign + t.hh + ":" + t.mm;
  }

  function xmlEsc(s) {
    return String(s == null ? "" : s)
      .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  }

  // ---- ICC profiles ---------------------------------------------------------
  //
  // Two profiles are involved and they do different jobs:
  //
  //   image ICC     -- sRGB. Describes what our pixels ARE. Baked in below.
  //   output intent -- the press condition the file is destined for. Gelato asks
  //                    for GRACoL 2006 (a CMYK profile), which we cannot ship in
  //                    this repo, so it is loaded from a theme asset at runtime.
  //
  // If no output-intent profile is configured we fall back to using sRGB as the
  // output intent. That still produces a structurally valid PDF/X-4 -- an RGB
  // output condition is permitted -- and it is a large improvement on untagged
  // DeviceRGB, but it is NOT what Gelato asked for. configure() logs loudly in
  // that case so the gap doesn't go unnoticed.

  // sRGB IEC61966-2.1 (3144 bytes, the standard profile). Embedded rather than fetched
  // so a PDF can always be tagged even if a network request for a profile fails.
  var SRGB_ICC_B64 = [
    "AAAMSExpbm8CEAAAbW50clJHQiBYWVogB84AAgAJAAYAMQAAYWNzcE1TRlQAAAAASUVDIHNSR0IAAAAAAAAAAAAAAAEAAPbW",
    "AAEAAAAA0y1IUCAgAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAARY3BydAAAAVAAAAAz",
    "ZGVzYwAAAYQAAABsd3RwdAAAAfAAAAAUYmtwdAAAAgQAAAAUclhZWgAAAhgAAAAUZ1hZWgAAAiwAAAAUYlhZWgAAAkAAAAAU",
    "ZG1uZAAAAlQAAABwZG1kZAAAAsQAAACIdnVlZAAAA0wAAACGdmlldwAAA9QAAAAkbHVtaQAAA/gAAAAUbWVhcwAABAwAAAAk",
    "dGVjaAAABDAAAAAMclRSQwAABDwAAAgMZ1RSQwAABDwAAAgMYlRSQwAABDwAAAgMdGV4dAAAAABDb3B5cmlnaHQgKGMpIDE5",
    "OTggSGV3bGV0dC1QYWNrYXJkIENvbXBhbnkAAGRlc2MAAAAAAAAAEnNSR0IgSUVDNjE5NjYtMi4xAAAAAAAAAAAAAAASc1JH",
    "QiBJRUM2MTk2Ni0yLjEAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAFhZWiAAAAAA",
    "AADzUQABAAAAARbMWFlaIAAAAAAAAAAAAAAAAAAAAABYWVogAAAAAAAAb6IAADj1AAADkFhZWiAAAAAAAABimQAAt4UAABja",
    "WFlaIAAAAAAAACSgAAAPhAAAts9kZXNjAAAAAAAAABZJRUMgaHR0cDovL3d3dy5pZWMuY2gAAAAAAAAAAAAAABZJRUMgaHR0",
    "cDovL3d3dy5pZWMuY2gAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAZGVzYwAAAAAAAAAu",
    "SUVDIDYxOTY2LTIuMSBEZWZhdWx0IFJHQiBjb2xvdXIgc3BhY2UgLSBzUkdCAAAAAAAAAAAAAAAuSUVDIDYxOTY2LTIuMSBE",
    "ZWZhdWx0IFJHQiBjb2xvdXIgc3BhY2UgLSBzUkdCAAAAAAAAAAAAAAAAAAAAAAAAAAAAAGRlc2MAAAAAAAAALFJlZmVyZW5j",
    "ZSBWaWV3aW5nIENvbmRpdGlvbiBpbiBJRUM2MTk2Ni0yLjEAAAAAAAAAAAAAACxSZWZlcmVuY2UgVmlld2luZyBDb25kaXRp",
    "b24gaW4gSUVDNjE5NjYtMi4xAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAB2aWV3AAAAAAATpP4AFF8uABDPFAAD7cwABBML",
    "AANcngAAAAFYWVogAAAAAABMCVYAUAAAAFcf521lYXMAAAAAAAAAAQAAAAAAAAAAAAAAAAAAAAAAAAKPAAAAAnNpZyAAAAAA",
    "Q1JUIGN1cnYAAAAAAAAEAAAAAAUACgAPABQAGQAeACMAKAAtADIANwA7AEAARQBKAE8AVABZAF4AYwBoAG0AcgB3AHwAgQCG",
    "AIsAkACVAJoAnwCkAKkArgCyALcAvADBAMYAywDQANUA2wDgAOUA6wDwAPYA+wEBAQcBDQETARkBHwElASsBMgE4AT4BRQFM",
    "AVIBWQFgAWcBbgF1AXwBgwGLAZIBmgGhAakBsQG5AcEByQHRAdkB4QHpAfIB+gIDAgwCFAIdAiYCLwI4AkECSwJUAl0CZwJx",
    "AnoChAKOApgCogKsArYCwQLLAtUC4ALrAvUDAAMLAxYDIQMtAzgDQwNPA1oDZgNyA34DigOWA6IDrgO6A8cD0wPgA+wD+QQG",
    "BBMEIAQtBDsESARVBGMEcQR+BIwEmgSoBLYExATTBOEE8AT+BQ0FHAUrBToFSQVYBWcFdwWGBZYFpgW1BcUF1QXlBfYGBgYW",
    "BicGNwZIBlkGagZ7BowGnQavBsAG0QbjBvUHBwcZBysHPQdPB2EHdAeGB5kHrAe/B9IH5Qf4CAsIHwgyCEYIWghuCIIIlgiq",
    "CL4I0gjnCPsJEAklCToJTwlkCXkJjwmkCboJzwnlCfsKEQonCj0KVApqCoEKmAquCsUK3ArzCwsLIgs5C1ELaQuAC5gLsAvI",
    "C+EL+QwSDCoMQwxcDHUMjgynDMAM2QzzDQ0NJg1ADVoNdA2ODakNww3eDfgOEw4uDkkOZA5/DpsOtg7SDu4PCQ8lD0EPXg96",
    "D5YPsw/PD+wQCRAmEEMQYRB+EJsQuRDXEPURExExEU8RbRGMEaoRyRHoEgcSJhJFEmQShBKjEsMS4xMDEyMTQxNjE4MTpBPF",
    "E+UUBhQnFEkUahSLFK0UzhTwFRIVNBVWFXgVmxW9FeAWAxYmFkkWbBaPFrIW1hb6Fx0XQRdlF4kXrhfSF/cYGxhAGGUYihiv",
    "GNUY+hkgGUUZaxmRGbcZ3RoEGioaURp3Gp4axRrsGxQbOxtjG4obshvaHAIcKhxSHHscoxzMHPUdHh1HHXAdmR3DHeweFh5A",
    "HmoelB6+HukfEx8+H2kflB+/H+ogFSBBIGwgmCDEIPAhHCFIIXUhoSHOIfsiJyJVIoIiryLdIwojOCNmI5QjwiPwJB8kTSR8",
    "JKsk2iUJJTglaCWXJccl9yYnJlcmhya3JugnGCdJJ3onqyfcKA0oPyhxKKIo1CkGKTgpaymdKdAqAio1KmgqmyrPKwIrNitp",
    "K50r0SwFLDksbiyiLNctDC1BLXYtqy3hLhYuTC6CLrcu7i8kL1ovkS/HL/4wNTBsMKQw2zESMUoxgjG6MfIyKjJjMpsy1DMN",
    "M0YzfzO4M/E0KzRlNJ402DUTNU01hzXCNf02NzZyNq426TckN2A3nDfXOBQ4UDiMOMg5BTlCOX85vDn5OjY6dDqyOu87LTtr",
    "O6o76DwnPGU8pDzjPSI9YT2hPeA+ID5gPqA+4D8hP2E/oj/iQCNAZECmQOdBKUFqQaxB7kIwQnJCtUL3QzpDfUPARANER0SK",
    "RM5FEkVVRZpF3kYiRmdGq0bwRzVHe0fASAVIS0iRSNdJHUljSalJ8Eo3Sn1KxEsMS1NLmkviTCpMcky6TQJNSk2TTdxOJU5u",
    "TrdPAE9JT5NP3VAnUHFQu1EGUVBRm1HmUjFSfFLHUxNTX1OqU/ZUQlSPVNtVKFV1VcJWD1ZcVqlW91dEV5JX4FgvWH1Yy1ka",
    "WWlZuFoHWlZaplr1W0VblVvlXDVchlzWXSddeF3JXhpebF69Xw9fYV+zYAVgV2CqYPxhT2GiYfViSWKcYvBjQ2OXY+tkQGSU",
    "ZOllPWWSZedmPWaSZuhnPWeTZ+loP2iWaOxpQ2maafFqSGqfavdrT2una/9sV2yvbQhtYG25bhJua27Ebx5veG/RcCtwhnDg",
    "cTpxlXHwcktypnMBc11zuHQUdHB0zHUodYV14XY+dpt2+HdWd7N4EXhueMx5KnmJeed6RnqlewR7Y3vCfCF8gXzhfUF9oX4B",
    "fmJ+wn8jf4R/5YBHgKiBCoFrgc2CMIKSgvSDV4O6hB2EgITjhUeFq4YOhnKG14c7h5+IBIhpiM6JM4mZif6KZIrKizCLlov8",
    "jGOMyo0xjZiN/45mjs6PNo+ekAaQbpDWkT+RqJIRknqS45NNk7aUIJSKlPSVX5XJljSWn5cKl3WX4JhMmLiZJJmQmfyaaJrV",
    "m0Kbr5wcnImc951kndKeQJ6unx2fi5/6oGmg2KFHobaiJqKWowajdqPmpFakx6U4pammGqaLpv2nbqfgqFKoxKk3qamqHKqP",
    "qwKrdavprFys0K1ErbiuLa6hrxavi7AAsHWw6rFgsdayS7LCszizrrQltJy1E7WKtgG2ebbwt2i34LhZuNG5SrnCuju6tbsu",
    "u6e8IbybvRW9j74KvoS+/796v/XAcMDswWfB48JfwtvDWMPUxFHEzsVLxcjGRsbDx0HHv8g9yLzJOsm5yjjKt8s2y7bMNcy1",
    "zTXNtc42zrbPN8+40DnQutE80b7SP9LB00TTxtRJ1MvVTtXR1lXW2Ndc1+DYZNjo2WzZ8dp22vvbgNwF3IrdEN2W3hzeot8p",
    "36/gNuC94UThzOJT4tvjY+Pr5HPk/OWE5g3mlucf56noMui86Ubp0Opb6uXrcOv77IbtEe2c7ijutO9A78zwWPDl8XLx//KM",
    "8xnzp/Q09ML1UPXe9m32+/eK+Bn4qPk4+cf6V/rn+3f8B/yY/Sn9uv5L/tz/bf//"
  ].join("");
  var _srgb = null;
  function srgbIccBytes() {
    if (!_srgb) _srgb = iccBytesFromBase64(SRGB_ICC_B64);
    return _srgb;
  }

  var _config = { outputIntentUrl: "", outputIntent: null };
  var _oiPromise = null;

  /**
   * @param opts.outputIntentUrl  URL of a .icc for the output intent
   *                              (Gelato: GRACoL2006_Coated1v2.icc)
   * @param opts.identifier       e.g. "GRACoL2006_Coated1v2"
   * @param opts.condition/info   human-readable description
   */
  function configure(opts) {
    opts = opts || {};
    _config.outputIntentUrl = opts.outputIntentUrl || "";
    _config.identifier = opts.identifier || "GRACoL2006_Coated1v2";
    _config.condition = opts.condition ||
      "Coated GRACoL 2006 (ISO 12647-2:2004)";
    _config.info = opts.info || _config.condition;
    _oiPromise = null;
    if (!_config.outputIntentUrl) {
      console.warn("[pdfx4] No output-intent profile configured -- PDFs will " +
        "carry an sRGB output intent, not the GRACoL 2006 one Gelato specifies. " +
        "Upload GRACoL2006_Coated1v2.icc as a theme asset and pass its URL to " +
        "PDFX4.configure({ outputIntentUrl }).");
    }
    return this;
  }

  // Resolved once and cached; a failed fetch degrades to sRGB rather than
  // blocking an export the operator is waiting on.
  function outputIntent() {
    if (_oiPromise) return _oiPromise;
    _oiPromise = (function () {
      if (!_config.outputIntentUrl) {
        return Promise.resolve({
          iccBytes: srgbIccBytes(),
          identifier: "sRGB IEC61966-2.1",
          condition: "sRGB IEC61966-2.1",
          info: "sRGB IEC61966-2.1 (fallback -- GRACoL 2006 not configured)"
        });
      }
      return fetch(_config.outputIntentUrl)
        .then(function (r) {
          if (!r.ok) throw new Error("HTTP " + r.status);
          return r.arrayBuffer();
        })
        .then(function (buf) {
          var bytes = new Uint8Array(buf);
          iccComponents(bytes); // throws early if it isn't a usable profile
          return {
            iccBytes: bytes,
            identifier: _config.identifier,
            condition: _config.condition,
            info: _config.info
          };
        })
        .catch(function (e) {
          console.warn("[pdfx4] Output-intent profile failed to load (" +
            (e && e.message) + ") -- falling back to an sRGB output intent.");
          return {
            iccBytes: srgbIccBytes(),
            identifier: "sRGB IEC61966-2.1",
            condition: "sRGB IEC61966-2.1",
            info: "sRGB IEC61966-2.1 (fallback -- GRACoL 2006 failed to load)"
          };
        });
    })();
    return _oiPromise;
  }

  // ---- ICC helpers ----------------------------------------------------------

  function iccBytesFromBase64(b64) {
    var bin = global.atob(b64);
    var a = new Uint8Array(bin.length);
    for (var i = 0; i < bin.length; i++) a[i] = bin.charCodeAt(i);
    return a;
  }

  // An ICC profile declares its data colour space at byte offset 16 ('RGB ',
  // 'CMYK', 'GRAY'). The PDF /N key must match its component count or readers
  // reject the stream, so we read it from the profile rather than trusting the
  // caller to pass the right number.
  function iccComponents(icc) {
    if (!icc || icc.length < 20) throw new Error("pdfx4: ICC profile too short");
    var space = String.fromCharCode(icc[16], icc[17], icc[18], icc[19]);
    if (space === "CMYK") return 4;
    if (space === "RGB ") return 3;
    if (space === "GRAY") return 1;
    throw new Error("pdfx4: unsupported ICC colour space '" + space + "'");
  }

  function iccAlternate(n) {
    return n === 4 ? "/DeviceCMYK" : n === 1 ? "/DeviceGray" : "/DeviceRGB";
  }

  // ---- JPEG sanity ----------------------------------------------------------
  // /DCTDecode + /ColorSpace [/ICCBased sRGB] only decodes correctly if the JPEG
  // really is 3-component baseline. Canvas always gives us that, but a bad
  // assumption here produces a colour-mangled print rather than an error, so we
  // read the SOF marker and check.
  function jpegComponents(u8) {
    if (u8[0] !== 0xFF || u8[1] !== 0xD8) throw new Error("pdfx4: not a JPEG");
    var i = 2;
    while (i < u8.length - 1) {
      if (u8[i] !== 0xFF) { i++; continue; }
      var marker = u8[i + 1];
      if (marker === 0xD8 || marker === 0x01 || (marker >= 0xD0 && marker <= 0xD7)) { i += 2; continue; }
      if (marker === 0xD9) break;
      var len = (u8[i + 2] << 8) | u8[i + 3];
      // SOF0/1/2/3, 9/10/11, 13/14/15 -- but never DHT(C4)/JPG(C8)/DAC(CC)
      if (marker >= 0xC0 && marker <= 0xCF &&
          marker !== 0xC4 && marker !== 0xC8 && marker !== 0xCC) {
        return { components: u8[i + 9], height: (u8[i + 5] << 8) | u8[i + 6], width: (u8[i + 7] << 8) | u8[i + 8] };
      }
      i += 2 + len;
    }
    throw new Error("pdfx4: no JPEG SOF marker found");
  }

  function dataUrlToBytes(dataUrl) {
    var comma = dataUrl.indexOf(",");
    if (comma < 0) throw new Error("pdfx4: malformed data URL");
    return iccBytesFromBase64(dataUrl.slice(comma + 1));
  }

  // ---- the document ---------------------------------------------------------
  //
  // Object plan (fixed numbering keeps the cross-references readable):
  //    1 Catalog        5 Image XObject      9  XMP metadata
  //    2 Pages          6 Image ICC (sRGB)   10 Info
  //    3 Page           7 OutputIntent
  //    4 Contents       8 OutputIntent ICC (DestOutputProfile)

  function build(opts) {
    opts = opts || {};

    var jpeg = opts.jpegBytes ||
      (opts.jpegDataUrl ? dataUrlToBytes(opts.jpegDataUrl) : null);
    if (!jpeg || !jpeg.length) throw new Error("pdfx4: jpegBytes/jpegDataUrl is required");

    var sof = jpegComponents(jpeg);
    if (sof.components !== 3) {
      throw new Error("pdfx4: expected a 3-component JPEG, got " + sof.components);
    }

    var imgIcc = opts.imageIccBytes;
    if (!imgIcc || !imgIcc.length) throw new Error("pdfx4: imageIccBytes (sRGB) is required");
    var imgN = iccComponents(imgIcc);
    if (imgN !== 3) throw new Error("pdfx4: the image ICC profile must be RGB");

    var oi = opts.outputIntent || {};
    var oiIcc = oi.iccBytes;
    if (!oiIcc || !oiIcc.length) throw new Error("pdfx4: outputIntent.iccBytes is required");
    var oiN = iccComponents(oiIcc);

    var pageW = (opts.pageWidthMm || 0) * MM_TO_PT;
    var pageH = (opts.pageHeightMm || 0) * MM_TO_PT;
    if (!(pageW > 0 && pageH > 0)) throw new Error("pdfx4: pageWidthMm/pageHeightMm are required");

    var bleed = (opts.bleedMm || 0) * MM_TO_PT;
    if (bleed < 0 || bleed * 2 >= pageW || bleed * 2 >= pageH) {
      throw new Error("pdfx4: bleedMm does not fit inside the page");
    }

    // Not `instanceof Date`: that compares against this realm's Date, so a Date
    // handed in from another context (an iframe, or the test harness's vm) would
    // be silently ignored and replaced with "now".
    var isDate = Object.prototype.toString.call(opts.date) === "[object Date]";
    var now = isDate && !isNaN(opts.date.getTime()) ? opts.date : new Date();
    var title = opts.title || "Poster";
    var creator = opts.creator || PRODUCER;

    var w = new Writer();
    var offsets = [];            // offsets[objNum] = byte offset of "N 0 obj"
    var OBJ_COUNT = 10;

    function begin(n) { offsets[n] = w.len; w.line(n + " 0 obj"); }
    function end() { w.line("endobj"); }

    function stream(n, dict, payload) {
      begin(n);
      w.line("<< " + dict + " /Length " + payload.length + " >>");
      w.line("stream");
      w.push(payload);
      w.line("");            // guarantee endstream starts on a fresh line
      w.line("endstream");
      end();
    }

    // --- header. The binary comment marks the file as non-ASCII so naive
    //     transports don't line-ending-mangle the JPEG payload.
    w.line("%PDF-1.6");
    w.push(new Uint8Array([0x25, 0xE2, 0xE3, 0xCF, 0xD3])); w.line("");

    // --- 1 Catalog
    begin(1);
    w.line("<< /Type /Catalog /Pages 2 0 R /OutputIntents [7 0 R] /Metadata 9 0 R >>");
    end();

    // --- 2 Pages
    begin(2);
    w.line("<< /Type /Pages /Kids [3 0 R] /Count 1 >>");
    end();

    // --- 3 Page. MediaBox and BleedBox are the full sheet; TrimBox is inset by
    //     the bleed and is the line the printer actually cuts on.
    var trim = "[" + num(bleed) + " " + num(bleed) + " " +
      num(pageW - bleed) + " " + num(pageH - bleed) + "]";
    var media = "[0 0 " + num(pageW) + " " + num(pageH) + "]";
    begin(3);
    w.line("<< /Type /Page /Parent 2 0 R");
    w.line("   /MediaBox " + media);
    w.line("   /BleedBox " + media);
    w.line("   /TrimBox " + trim);
    w.line("   /Resources << /XObject << /Im0 5 0 R >> /ProcSet [/PDF /ImageC] >>");
    w.line("   /Contents 4 0 R >>");
    end();

    // --- 4 Contents. One image, scaled to the full sheet. Deliberately no
    //     colour operators -- see the bleed note in the file header.
    var content = latin1("q\n" + num(pageW) + " 0 0 " + num(pageH) + " 0 0 cm\n/Im0 Do\nQ\n");
    stream(4, "", content);

    // --- 5 Image XObject, tagged ICCBased sRGB. Tagging the IMAGE (not just the
    //     document) is what stops a RIP or Photoshop reinterpreting the pixels in
    //     whatever RGB working space it happens to prefer.
    stream(5,
      "/Type /XObject /Subtype /Image /Width " + sof.width + " /Height " + sof.height +
      " /ColorSpace [/ICCBased 6 0 R] /BitsPerComponent 8 /Filter /DCTDecode",
      jpeg);

    // --- 6 sRGB profile backing the image colour space
    stream(6, "/N " + imgN + " /Alternate " + iccAlternate(imgN), imgIcc);

    // --- 7 OutputIntent. /S /GTS_PDFX is the PDF/X marker; /GTS_PDFA1 (what the
    //     old code emitted) is the PDF/A one and does not satisfy PDF/X-4.
    begin(7);
    w.line("<< /Type /OutputIntent /S /GTS_PDFX");
    w.line("   /OutputConditionIdentifier " + pdfStr(oi.identifier || "Custom"));
    w.line("   /OutputCondition " + pdfStr(oi.condition || oi.identifier || "Custom"));
    w.line("   /RegistryName " + pdfStr(oi.registryName || "http://www.color.org"));
    w.line("   /Info " + pdfStr(oi.info || oi.identifier || "Custom"));
    w.line("   /DestOutputProfile 8 0 R >>");
    end();

    // --- 8 the output intent's embedded profile (GRACoL 2006 for Gelato)
    stream(8, "/N " + oiN + " /Alternate " + iccAlternate(oiN), oiIcc);

    // --- 9 XMP. PDF/X-4 is identified here as well as in the Info dict, and a
    //     preflight will fail the file if the two disagree.
    var xmp =
      '<?xpacket begin="﻿" id="W5M0MpCehiHzreSzNTczkc9d"?>\n' +
      '<x:xmpmeta xmlns:x="adobe:ns:meta/">\n' +
      ' <rdf:RDF xmlns:rdf="http://www.w3.org/1999/02/22-rdf-syntax-ns#">\n' +
      '  <rdf:Description rdf:about="" xmlns:pdfxid="http://www.npes.org/pdfx/ns/id/">\n' +
      '   <pdfxid:GTS_PDFXVersion>PDF/X-4</pdfxid:GTS_PDFXVersion>\n' +
      '  </rdf:Description>\n' +
      '  <rdf:Description rdf:about="" xmlns:pdf="http://ns.adobe.com/pdf/1.3/">\n' +
      '   <pdf:Producer>' + xmlEsc(PRODUCER) + '</pdf:Producer>\n' +
      '   <pdf:Trapped>False</pdf:Trapped>\n' +
      '  </rdf:Description>\n' +
      '  <rdf:Description rdf:about="" xmlns:xmp="http://ns.adobe.com/xap/1.0/">\n' +
      '   <xmp:CreatorTool>' + xmlEsc(creator) + '</xmp:CreatorTool>\n' +
      '   <xmp:CreateDate>' + xmpDate(now) + '</xmp:CreateDate>\n' +
      '   <xmp:ModifyDate>' + xmpDate(now) + '</xmp:ModifyDate>\n' +
      '   <xmp:MetadataDate>' + xmpDate(now) + '</xmp:MetadataDate>\n' +
      '  </rdf:Description>\n' +
      '  <rdf:Description rdf:about="" xmlns:dc="http://purl.org/dc/elements/1.1/">\n' +
      '   <dc:format>application/pdf</dc:format>\n' +
      '   <dc:title><rdf:Alt><rdf:li xml:lang="x-default">' + xmlEsc(title) +
      '</rdf:li></rdf:Alt></dc:title>\n' +
      '  </rdf:Description>\n' +
      ' </rdf:RDF>\n' +
      '</x:xmpmeta>\n' +
      '<?xpacket end="w"?>';
    stream(9, "/Type /Metadata /Subtype /XML", utf8(xmp));

    // --- 10 Info
    begin(10);
    w.line("<< /Title " + pdfStr(title));
    w.line("   /Creator " + pdfStr(creator));
    w.line("   /Producer " + pdfStr(PRODUCER));
    w.line("   /CreationDate " + pdfStr(pdfDate(now)));
    w.line("   /ModDate " + pdfStr(pdfDate(now)));
    w.line("   /GTS_PDFXVersion (PDF/X-4)");
    w.line("   /Trapped /False >>");
    end();

    // --- xref. Every entry is exactly 20 bytes: 10-digit offset, space,
    //     5-digit generation, space, type, space, newline.
    var xrefAt = w.len;
    w.line("xref");
    w.line("0 " + (OBJ_COUNT + 1));
    w.raw("0000000000 65535 f \n");
    for (var i = 1; i <= OBJ_COUNT; i++) {
      if (offsets[i] == null) throw new Error("pdfx4: object " + i + " was never written");
      w.raw(pad(offsets[i], 10) + " 00000 n \n");
    }

    // --- trailer. PDF/X requires a file identifier; both halves are equal
    //     because this is an original file, never an incremental update.
    var id = fileId(jpeg, now);
    w.line("trailer");
    w.line("<< /Size " + (OBJ_COUNT + 1) + " /Root 1 0 R /Info 10 0 R /ID [<" + id + "> <" + id + ">] >>");
    w.line("startxref");
    w.line(String(xrefAt));
    w.raw("%%EOF\n");

    return new Blob([w.bytes()], { type: "application/pdf" });
  }

  // 16 bytes of hex derived from the payload + timestamp. This only has to be
  // unique-ish per file, not cryptographic.
  function fileId(jpeg, date) {
    var h1 = 0x811c9dc5, h2 = 0x01000193 ^ date.getTime();
    var step = Math.max(1, Math.floor(jpeg.length / 4096));
    for (var i = 0; i < jpeg.length; i += step) {
      h1 = ((h1 ^ jpeg[i]) * 16777619) >>> 0;
      h2 = ((h2 + jpeg[i]) * 2654435761) >>> 0;
    }
    var len = jpeg.length >>> 0;
    return (pad(h1.toString(16), 8) + pad(h2.toString(16), 8) +
      pad(len.toString(16), 8) + pad((date.getTime() >>> 0).toString(16), 8)).slice(0, 32);
  }

  // ---- canvas -> full-bleed canvas -----------------------------------------
  // FALLBACK ONLY -- prefer opts.sourceIncludesBleed (the caller renders the
  // bleed). Grows a trim-sized render out to trim+bleed by MIRRORING the
  // outermost `bleedPx` of artwork outwards.
  // Two earlier approaches both showed on the sheet: a rect filled with the
  // sampled corner colour (flat, no paper texture, a visible seam against the
  // textured poster edge), and stretching the single outermost row/column across
  // the whole 4 mm (the texture smeared into streaks). A reflection carries real
  // edge pixels outwards at their true scale, so a pattern continues.
  // `releaseSource` frees the source canvas's backing store once it has been
  // copied. At 300 DPI a 70x100 poster is ~98 megapixels (~390 MB as RGBA), so
  // holding the original and the padded copy at the same time is a real
  // out-of-memory risk on a laptop. Only pass it when the caller is finished
  // with the source.
  function padCanvasToBleed(src, bleedPx, releaseSource) {
    bleedPx = Math.round(bleedPx || 0);
    if (bleedPx <= 0) return src;

    var w = src.width, h = src.height, b = bleedPx;
    // How much real artwork there is to reflect. Equals b for any real poster
    // (4 mm at 300 DPI is 47 px against thousands); the clamp only bites on a
    // pathologically small canvas, where the strip gets scaled to fill instead.
    var sb = Math.max(1, Math.min(b, w, h));

    var out = document.createElement("canvas");
    out.width = w + b * 2;
    out.height = h + b * 2;
    var ctx = out.getContext("2d");
    ctx.drawImage(src, b, b);

    // Draw a source strip into a destination rect, flipped on either axis: the
    // flip is what makes it a reflection through the artwork's edge rather than
    // a repeat of it (a repeat would put a hard seam right on the trim line).
    function strip(sx, sy, sw, sh, dx, dy, dw, dh, fx, fy) {
      ctx.save();
      ctx.translate(dx + (fx ? dw : 0), dy + (fy ? dh : 0));
      ctx.scale(fx ? -1 : 1, fy ? -1 : 1);
      ctx.drawImage(src, sx, sy, sw, sh, 0, 0, dw, dh);
      ctx.restore();
    }
    var R = b + w, B = b + h;                                      // artwork right/bottom edges
    strip(0, 0, w, sb, b, 0, w, b, false, true);                   // top
    strip(0, h - sb, w, sb, b, B, w, b, false, true);              // bottom
    strip(0, 0, sb, h, 0, b, b, h, true, false);                   // left
    strip(w - sb, 0, sb, h, R, b, b, h, true, false);              // right
    strip(0, 0, sb, sb, 0, 0, b, b, true, true);                   // top-left
    strip(w - sb, 0, sb, sb, R, 0, b, b, true, true);              // top-right
    strip(0, h - sb, sb, sb, 0, B, b, b, true, true);              // bottom-left
    strip(w - sb, h - sb, sb, sb, R, B, b, b, true, true);         // bottom-right

    if (releaseSource) { src.width = 0; src.height = 0; }
    return out;
  }

  /**
   * The one call site everything else uses.
   *
   * @param canvas   render at print resolution: trim-sized, or the full sheet
   *                 already when opts.sourceIncludesBleed is set
   * @param opts.trimWidthMm/trimHeightMm  finished poster size
   * @param opts.bleedMm                   per-edge bleed (Gelato: 4)
   * @param opts.sourceIncludesBleed       the canvas already covers trim+bleed
   *                 (the caller rendered it) -- do not pad it here
   * @param opts.jpegQuality               default 0.95
   * @param opts.imageIccBytes             sRGB profile (required)
   * @param opts.outputIntent              { iccBytes, identifier, condition, info }
   */
  function fromCanvas(canvas, opts) {
    opts = opts || {};
    var bleedMm = opts.bleedMm || 0;
    var trimW = opts.trimWidthMm, trimH = opts.trimHeightMm;
    if (!(trimW > 0 && trimH > 0)) throw new Error("pdfx4: trimWidthMm/trimHeightMm are required");

    // A caller that rendered its own bleed hands us the finished sheet, so use it
    // as is. Otherwise derive the bleed in pixels from the render's own scale, so
    // the padding is exactly `bleedMm` whatever DPI it was drawn at.
    var padded;
    if (opts.sourceIncludesBleed) {
      padded = canvas;
    } else {
      var pxPerMm = canvas.width / trimW;
      padded = padCanvasToBleed(canvas, bleedMm * pxPerMm, opts.releaseSource);
    }

    // Encode first, then let the caller's canvas go: a 70x100 sheet at 300 DPI is
    // ~100 megapixels (~400 MB as RGBA) and the JPEG string is the only copy the
    // rest of this function needs.
    var jpegDataUrl = padded.toDataURL("image/jpeg", opts.jpegQuality || 0.95);
    if (opts.releaseSource && padded === canvas) { canvas.width = 0; canvas.height = 0; }

    return build({
      jpegDataUrl: jpegDataUrl,
      pageWidthMm: trimW + bleedMm * 2,
      pageHeightMm: trimH + bleedMm * 2,
      bleedMm: bleedMm,
      imageIccBytes: opts.imageIccBytes,
      outputIntent: opts.outputIntent,
      title: opts.title,
      creator: opts.creator,
      date: opts.date
    });
  }

  /**
   * The everyday entry point: hand it a trim-sized canvas and the finished
   * poster size, get back a PDF/X-4 Blob. Resolves the configured output-intent
   * profile first (cached after the first call).
   */
  function exportCanvas(canvas, opts) {
    opts = opts || {};
    return outputIntent().then(function (oi) {
      return fromCanvas(canvas, {
        trimWidthMm: opts.trimWidthMm,
        trimHeightMm: opts.trimHeightMm,
        bleedMm: opts.bleedMm != null ? opts.bleedMm : 4,
        sourceIncludesBleed: !!opts.sourceIncludesBleed,
        jpegQuality: opts.jpegQuality,
        releaseSource: opts.releaseSource,
        imageIccBytes: srgbIccBytes(),
        outputIntent: oi,
        title: opts.title,
        creator: opts.creator
      });
    });
  }

  global.PDFX4 = {
    configure: configure,
    exportCanvas: exportCanvas,
    outputIntent: outputIntent,
    srgbIccBytes: srgbIccBytes,
    build: build,
    fromCanvas: fromCanvas,
    padCanvasToBleed: padCanvasToBleed,
    iccBytesFromBase64: iccBytesFromBase64,
    iccComponents: iccComponents,
    MM_TO_PT: MM_TO_PT
  };
})(window);
